import { Mail, Phone, MapPin, ExternalLink, Heart } from "lucide-react";
import { ThemeLogo } from "./ThemeLogo";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 relative overflow-hidden transition-colors duration-300"
      style={{ boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.08)" }}
    >
      {/* Enhanced background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-100/40 dark:bg-red-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-200/40 dark:bg-red-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <div className="flex items-center mb-6">
                <ThemeLogo
                  alt="Kartly Logo"
                  className="footer-logo mr-4"
                  style={{ objectFit: "contain" }}
                />
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-6 max-w-md">
                Intelligent eCommerce solutions powered by cutting-edge AI
                technology. Transform your business with smarter, scalable
                commerce experiences.
              </p>
            </div>

            {/* Solutions Column */}
            <div className="slide-in-left" style={{ animationDelay: "300ms" }}>
              <h3 className="text-lg font-semibold mb-6 text-gray-800 dark:text-gray-100 section-subheading">
                Solutions
              </h3>
              <ul className="space-y-4">
                {[
                  "AI Personalization",
                  "Cloud Infrastructure",
                  "Analytics & Insights",
                  "Mobile Commerce",
                  "Automation Tools",
                  "Integration Platform",
                ].map((item, index) => (
                  <li
                    key={item}
                    className="hover-lift"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <a
                      href="#"
                      className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors duration-300 flex items-center group hover-glow"
                    >
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {item}
                      </span>
                      <ExternalLink
                        size={14}
                        className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 wiggle"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Column */}
            <div className="slide-in-right" style={{ animationDelay: "600ms" }}>
              <h3 className="text-lg font-semibold mb-6 text-gray-800 dark:text-gray-100 section-subheading">
                Contact
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3 hover-lift">
                  <Mail
                    size={18}
                    className="text-red-500 mt-1 flex-shrink-0 pulse-subtle"
                  />
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Email</p>
                    <a
                      href="mailto:info@kartly.ai"
                      className="text-gray-800 dark:text-gray-200 hover:text-red-500 transition-colors hover-glow"
                    >
                      info@kartly.ai
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3 hover-lift">
                  <Phone
                    size={18}
                    className="text-red-600 mt-1 flex-shrink-0 pulse-subtle"
                    style={{ animationDelay: "1s" }}
                  />
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Phone</p>
                    <a
                      href="tel:+12015654096"
                      className="text-gray-800 dark:text-gray-200 hover:text-red-600 transition-colors hover-glow"
                    >
                      +1 (201) 565-4096
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3 hover-lift">
                  <MapPin
                    size={18}
                    className="text-red-500 mt-1 flex-shrink-0 wiggle"
                  />
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Location</p>
                    <p className="text-gray-800 dark:text-gray-200">
                      Delaware, USA
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 mb-2 md:mb-0">
                  <a
                    href="https://twitter.com/kartlyai"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                    className="hover:text-red-400 transition-colors hover-glow flex items-center"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M22.46 5.93c-.79.35-1.64.59-2.53.69a4.48 4.48 0 0 0 1.97-2.48 8.94 8.94 0 0 1-2.83 1.08A4.48 4.48 0 0 0 16.11 4c-2.48 0-4.49 2.01-4.49 4.49 0 .35.04.7.11 1.03C7.69 9.36 4.07 7.6 1.64 4.93c-.38.65-.6 1.4-.6 2.2 0 1.52.77 2.86 1.95 3.65-.72-.02-1.39-.22-1.98-.55v.06c0 2.13 1.52 3.91 3.54 4.31-.37.1-.76.16-1.16.16-.28 0-.55-.03-.81-.08.55 1.72 2.16 2.97 4.07 3a9.01 9.01 0 0 1-5.59 1.93c-.36 0-.71-.02-1.06-.06A12.77 12.77 0 0 0 7.29 21c8.29 0 12.83-6.87 12.83-12.83 0-.2 0-.41-.01-.61.88-.64 1.64-1.44 2.25-2.36z" />
                    </svg>
                    <span className="sr-only">Twitter</span>
                    <span className="ml-1 text-xs font-medium">Twitter</span>
                  </a>
                  <a
                    href="https://linkedin.com/company/kartlyai"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="hover:text-red-600 transition-colors hover-glow flex items-center"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.28c-.97 0-1.75-.79-1.75-1.75s.78-1.75 1.75-1.75 1.75.79 1.75 1.75-.78 1.75-1.75 1.75zm15.5 11.28h-3v-5.6c0-1.34-.03-3.07-1.87-3.07-1.87 0-2.16 1.46-2.16 2.97v5.7h-3v-10h2.88v1.36h.04c.4-.75 1.38-1.54 2.84-1.54 3.04 0 3.6 2 3.6 4.59v5.59z" />
                    </svg>
                    <span className="sr-only">LinkedIn</span>
                    <span className="ml-1 text-xs font-medium">LinkedIn</span>
                  </a>
                  <a
                    href="https://facebook.com/kartlyai"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="hover:text-red-500 transition-colors hover-glow flex items-center"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M22.675 0h-21.35c-.733 0-1.325.592-1.325 1.326v21.348c0 .733.592 1.326 1.325 1.326h11.495v-9.294h-3.128v-3.622h3.128v-2.672c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12v9.294h6.116c.733 0 1.325-.593 1.325-1.326v-21.349c0-.734-.592-1.326-1.325-1.326z" />
                    </svg>
                    <span className="sr-only">Facebook</span>
                    <span className="ml-1 text-xs font-medium">Facebook</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          {/* Bottom Bar */}
          <div className="w-full border-t border-gray-200 dark:border-gray-700 mt-12" />
          <div
            className="container mx-auto px-4 sm:px-6 lg:px-8 py-8"
            style={{
              position: "relative",
              bottom: 0,
              left: 0,
              right: 0,
              width: "100%",
              marginTop: "auto",
              background: "inherit",
            }}
          >
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              {/* Copyright & Details */}
              <div className="flex flex-col md:flex-row md:items-center md:space-x-8 text-gray-600 dark:text-gray-400 fade-in-up text-sm w-full justify-between">
                <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-2 md:mb-0">
                  <span>© {currentYear} Kartly. All rights reserved.</span>
                </div>

                <div className="flex flex-wrap items-center gap-3 md:gap-6 mt-2 md:mt-0 w-full md:w-auto justify-center md:justify-start">
                  <a
                    href="#"
                    className="px-3 py-1 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm hover-glow focus:outline-none focus:ring-2 focus:ring-red-400"
                  >
                    Privacy Policy
                  </a>
                  <span className="hidden md:inline-block text-gray-400">
                    |
                  </span>
                  <a
                    href="#"
                    className="px-3 py-1 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm hover-glow focus:outline-none focus:ring-2 focus:ring-red-400"
                  >
                    Terms of Service
                  </a>
                  <span className="hidden md:inline-block text-gray-400">
                    |
                  </span>
                  <a
                    href="#"
                    className="px-3 py-1 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm hover-glow focus:outline-none focus:ring-2 focus:ring-red-400"
                  >
                    Security
                  </a>
                </div>
                <div className="flex items-center space-x-2 mt-2 md:mt-0">
                  <span className="italic text-xs text-gray-400 dark:text-gray-500">
                    Smart, Scalable Commerce • Powered by AI
                  </span>
                  <span className="hidden md:inline-block wiggle">•</span>
                  <span className="font-semibold">Version:</span>
                  <span>v1.0.0</span>
                </div>
              </div>
            </div>
          </div>
          {/* Bottom Bar ends*/}
        </div>
      </div>
    </footer>
  );
}
